import { useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'
import { UNSAFE_DataRouterContext, useBeforeUnload, useBlocker, type BlockerFunction } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { EditorActivityContext, useEditorActivity, type EditorActivity } from '@/lib/editor-activity'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalDescription,
  ModalFooter,
} from '@/components/ui/modal'

/** One navigation decision for all independently saved editors in this workspace. */
export function UnsavedChangesScope({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const [activities, setActivities] = useState<ReadonlyMap<string, EditorActivity>>(new Map())
  const update = useCallback((id: string, activity: EditorActivity | null) => {
    setActivities(previous => {
      const next = new Map(previous)
      if (activity) next.set(id, activity)
      else next.delete(id)
      return next
    })
  }, [])
  const value = useMemo(() => ({ activities, update }), [activities, update])
  const active = [...activities.values()].filter(activity => activity.dirty || activity.pending)
  const pending = active.some(activity => activity.pending)
  const transition = useRef<{ from: string; to: string } | null>(null)
  const losesDraft = (activity: EditorActivity) => activity.dirty && !(activity.preserveWithinPath && activity.preserveWithinPath === transition.current?.from && activity.preserveWithinPath === transition.current?.to)
  return (
    <EditorActivityContext.Provider value={value}>
      <StandaloneGuard
        dirty={active.length > 0}
        pending={pending}
        description={t(pending ? 'eventSettings.pendingNavigation' : 'eventSettings.dirtyNavigation')}
        onDiscard={() => active.filter(losesDraft).forEach(activity => activity.onDiscard?.())}
        // One editor holding everything unsaved, and able to save it all: then «Lagre og gå videre».
        onSave={active.length === 1 && !pending ? active[0].onSave : undefined}
        shouldBlock={({ currentLocation, nextLocation }) => {
          transition.current = { from: currentLocation.pathname, to: nextLocation.pathname }
          return pending || active.some(losesDraft)
        }}
      />
      {children}
    </EditorActivityContext.Provider>
  )
}

export function EditorActivityStatus({ children }: { children?: ReactNode }) {
  const { t } = useTranslation()
  const context = useContext(EditorActivityContext)
  const active = [...(context?.activities.values() ?? [])].filter(activity => activity.dirty || activity.pending)
  if (!active.length) return children ?? null
  return <p role="status" className="text-small text-[var(--text-secondary)]">{t(active.some(activity => activity.pending) ? 'eventSettings.pendingSummary' : 'eventSettings.dirtySummary')}</p>
}

/** Data-router navigation and browser reload share the same dirty-state contract. */
export function UnsavedChangesGuard({
  dirty,
  description,
  pending = false,
  onDiscard,
  onSave,
  preserveWithinPath,
}: {
  dirty: boolean
  description: string
  pending?: boolean
  onDiscard?: () => void
  /** Saves and then leaves, when the page can save everything that is unsaved. Resolves true on an
   *  acknowledged save; on false the dialog stays, so nothing is lost. */
  onSave?: () => Promise<boolean>
  preserveWithinPath?: string
}) {
  const scoped = useEditorActivity({ dirty, pending, description, onDiscard, onSave, preserveWithinPath })
  return scoped ? null : <StandaloneGuard dirty={dirty} pending={pending} description={description} onDiscard={onDiscard} onSave={onSave} />
}

function StandaloneGuard({ dirty, pending = false, description, onDiscard, onSave, shouldBlock }: EditorActivity & { shouldBlock?: BlockerFunction }) {
  const router = useContext(UNSAFE_DataRouterContext)
  useBeforeUnload((event) => {
    if (!dirty && !pending) return
    event.preventDefault()
    event.returnValue = ''
  })
  return router ? (
    <RouteGuard dirty={dirty || pending} description={description} pending={pending} onDiscard={onDiscard} onSave={onSave} shouldBlock={shouldBlock} />
  ) : null
}

function RouteGuard({
  dirty,
  description,
  pending,
  onDiscard,
  onSave,
  shouldBlock,
}: {
  dirty: boolean
  description: string
  pending: boolean
  onDiscard?: () => void
  onSave?: () => Promise<boolean>
  shouldBlock?: BlockerFunction
}) {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)
  const origin = useRef<HTMLElement | null>(null)
  const blocker = useBlocker(
    (transition) => {
      const { currentLocation, nextLocation } = transition
      const blocked = (
      dirty &&
      `${currentLocation.pathname}${currentLocation.search}` !==
        `${nextLocation.pathname}${nextLocation.search}` && (!shouldBlock || shouldBlock(transition))
      )
      if (blocked) origin.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
      return blocked
    },
  )
  return (
    <Modal
      open={blocker.state === 'blocked'}
      onOpenChange={(open) => {
        if (!open && blocker.state === 'blocked') blocker.reset()
      }}
    >
      <ModalContent size="sm" onCloseAutoFocus={event => {
        event.preventDefault()
        const target = origin.current
        if (!target?.isConnected) return
        if (target.getClientRects().length) target.focus()
        else {
          const listId = target.closest('[role="tablist"]')?.id
          const disclosure = listId ? document.querySelector<HTMLButtonElement>(`button[aria-controls="${CSS.escape(listId)}"]`) : null
          if (disclosure?.getClientRects().length) disclosure.focus()
        }
      }}>
        <ModalHeader>
          <ModalTitle>{t('eventWorkspace.leaveTitle')}</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <ModalDescription>{description}</ModalDescription>
        </ModalBody>
        <ModalFooter>
          {onSave ? (
            <Button
              loading={saving}
              disabled={pending && !saving}
              onClick={async () => {
                if (blocker.state !== 'blocked') return
                setSaving(true)
                const ok = await onSave().catch(() => false)
                setSaving(false)
                // Saved: go on. Not saved (a field refused, the write failed): stay, so the page shows why.
                if (blocker.state === 'blocked') {
                  if (ok) blocker.proceed()
                  else blocker.reset()
                }
              }}
            >
              {t('eventWorkspace.saveAndLeave')}
            </Button>
          ) : null}
          <Button
            variant={onSave ? 'secondary' : undefined}
            onClick={() => {
              if (blocker.state === 'blocked') blocker.reset()
            }}
          >
            {t('eventWorkspace.stay')}
          </Button>
          <Button
            variant="secondary"
            disabled={pending}
            onClick={() => {
              if (blocker.state === 'blocked') {
                onDiscard?.()
                blocker.proceed()
              }
            }}
          >
            {t('eventWorkspace.discard')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
